import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { questionAPI } from '../../services/api';
import UserMenu from '../UserMenu';
import { HeartIcon, ClockIcon } from '@heroicons/react/24/outline';
import { useSubscriptionStatus } from '../../hooks/useSubscriptionStatus';
import { MINGU_ENABLED } from '../../config/features';
import { useRememberedScroll, useSubjects, METHODS_COLOUR } from '../../subjects/SubjectContext';
import { SubjectDropdown, SubjectPathHeader, SubjectSidebar } from '../../subjects/SubjectNav';
import SubjectFirstRun from '../../subjects/SubjectFirstRun';

interface TopicStats {
  topic: string;
  total_questions: number;
  average_difficulty: number;
}

const Dashboard: React.FC = () => {
  const { user } = useAuth();
  const { active, payload } = useSubjects();
  const subjectColour = active?.colour ?? METHODS_COLOUR;
  const firstRun = active?.enrolment.first_run ?? false;
  const [topicStats, setTopicStats] = useState<TopicStats[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const { subscriptionStatus, isLoading: subscriptionStatusLoading } = useSubscriptionStatus();
  const isFreePlan = subscriptionStatus?.is_on_free_plan ?? false;
  const freeUsage = subscriptionStatus?.free_tier_usage;
  const freeUsageResetLabel = useMemo(() => {
    if (!freeUsage) return null;
    try {
      const resetDate = new Date(freeUsage.resets_at);
      return resetDate.toLocaleString(undefined, {
        weekday: 'long',
        hour: 'numeric',
        minute: '2-digit',
      });
    } catch {
      return freeUsage.resets_on;
    }
  }, [freeUsage]);

  const practiceSection = (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow mb-8">
      <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
        <h2 className="text-lg font-medium text-gray-900 dark:text-white">Practice Mode</h2>
      </div>
      <div className="p-6">
        <Link
          to="/practice"
          className="block p-5 border border-gray-200 dark:border-gray-700 rounded-lg hover:border-subject-500 hover:bg-subject-50 dark:hover:bg-subject-900/20 transition-colors text-left"
        >
          <div className="flex items-center mb-2">
            <div className="p-2 bg-green-100 dark:bg-green-900 rounded">
              <svg className="h-6 w-6 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
              </svg>
            </div>
            <h3 className="ml-3 text-lg font-semibold text-gray-900 dark:text-white">Start Practicing</h3>
          </div>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Random questions with worked solutions, tailored to your progress.
          </p>
        </Link>
      </div>
    </div>
  );

  const gameModesSection = (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow mb-8">
      <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
        <h2 className="text-lg font-medium text-gray-900 dark:text-white">Game Modes</h2>
      </div>
      <div className="p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Link
            to="/survival"
            className="block p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:border-subject-500 hover:bg-subject-50 dark:hover:bg-subject-900/20 transition-colors text-left"
          >
            <div className="flex items-center mb-2">
              <div className="p-1 bg-red-100 dark:bg-red-900 rounded">
                <HeartIcon className="h-5 w-5 text-red-600 dark:text-red-400" />
              </div>
              <h3 className="ml-2 font-medium text-gray-900 dark:text-white">Survival Mode</h3>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              How many can you get right in a row?
            </p>
          </Link>

          <Link
            to="/rush"
            className="block p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:border-subject-500 hover:bg-subject-50 dark:hover:bg-subject-900/20 transition-colors text-left"
          >
            <div className="flex items-center mb-2">
              <div className="p-1 bg-blue-100 dark:bg-blue-900 rounded">
                <ClockIcon className="h-5 w-5 text-blue-600 dark:text-blue-400" />
              </div>
              <h3 className="ml-2 font-medium text-gray-900 dark:text-white">Rush Mode</h3>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Timed challenges with multiple durations
            </p>
          </Link>
        </div>
      </div>
    </div>
  );

  const skillCards = [
    {
      slug: 'differentiation',
      title: 'Differentiation',
      description: 'Chain, product, and quotient rule practice.',
      iconBg: 'bg-subject-100 dark:bg-subject-900',
      iconColor: 'text-subject-600 dark:text-subject-300',
      iconPath: 'M4 19h16M4 5h16M7 9h10M7 13h6',
    },
    {
      slug: 'trigonometry',
      title: 'Trigonometry',
      description: 'Solve trigonometric equations in a given domain.',
      iconBg: 'bg-indigo-100 dark:bg-indigo-900',
      iconColor: 'text-indigo-600 dark:text-indigo-300',
      iconPath: 'M3 12h18M12 3v18M7 7l10 10',
    },
    {
      slug: 'integration',
      title: 'Integration',
      description: 'Substitution, parts, and partial fractions practice.',
      iconBg: 'bg-emerald-100 dark:bg-emerald-900',
      iconColor: 'text-emerald-600 dark:text-emerald-300',
      iconPath: 'M4 12h16M12 4v16',
    },
    {
      slug: 'logarithms',
      title: 'Logarithms',
      description: 'Solve logarithmic equations.',
      iconBg: 'bg-amber-100 dark:bg-amber-900',
      iconColor: 'text-amber-600 dark:text-amber-300',
      iconPath: 'M5 12h14M12 5v14',
    },
    {
      slug: 'exponential',
      title: 'Exponential',
      description: 'Solve exponential equations with varied structures.',
      iconBg: 'bg-purple-100 dark:bg-purple-900',
      iconColor: 'text-purple-600 dark:text-purple-300',
      iconPath: 'M4 7h16M4 12h10M4 17h7',
    },
    {
      slug: 'quadratic',
      title: 'Quadratic',
      description: 'Solve quadratic equations (monic and non-monic).',
      iconBg: 'bg-sky-100 dark:bg-sky-900',
      iconColor: 'text-sky-600 dark:text-sky-300',
      iconPath: 'M4 6h16M4 12h12M4 18h8',
    },
    {
      slug: 'cubic',
      title: 'Cubic',
      description: 'Solve cubic equations by factoring.',
      iconBg: 'bg-rose-100 dark:bg-rose-900',
      iconColor: 'text-rose-600 dark:text-rose-300',
      iconPath: 'M4 8h16M4 12h14M4 16h10',
    },
    {
      slug: 'algebra',
      title: 'Algebra',
      description: 'Expansion, factorisation, and linear equations practice.',
      iconBg: 'bg-teal-100 dark:bg-teal-900',
      iconColor: 'text-teal-600 dark:text-teal-300',
      iconPath: 'M4 10h16M4 14h12M4 18h8',
    },
    {
      slug: 'transformations',
      title: 'Transformations',
      description: 'Apply or identify function transformations.',
      iconBg: 'bg-orange-100 dark:bg-orange-900',
      iconColor: 'text-orange-600 dark:text-orange-300',
      iconPath: 'M4 6h16M8 10h12M12 14h8',
    },
    {
      slug: 'graph_sketching',
      title: 'Graph Sketching',
      description: 'Sketch transformed graphs for common function types.',
      iconBg: 'bg-blue-100 dark:bg-blue-900',
      iconColor: 'text-blue-600 dark:text-blue-300',
      iconPath: 'M4 18h16M6 16l4-4 3 3 5-6',
    },
  ];

  const skillsSection = (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow mb-8">
      <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
        <h2 className="text-lg font-medium text-gray-900 dark:text-white">Skills</h2>
      </div>
      <div className="p-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
          {skillCards.map((skillCard) => (
            <Link
              key={skillCard.slug}
              to={`/skills/${skillCard.slug}`}
              className="block p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:border-subject-500 hover:bg-subject-50 dark:hover:bg-subject-900/20 transition-colors text-left"
            >
              <div className="flex items-center mb-2">
                <div className={`p-1 rounded ${skillCard.iconBg}`}>
                  <svg className={`h-5 w-5 ${skillCard.iconColor}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={skillCard.iconPath} />
                  </svg>
                </div>
                <h3 className="ml-2 font-medium text-gray-900 dark:text-white">{skillCard.title}</h3>
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {skillCard.description}
              </p>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );

  useEffect(() => {
    const loadDashboardData = async () => {
      if (subscriptionStatusLoading) {
        return;
      }

      if (isFreePlan) {
        setTopicStats([]);
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        const topicData = await questionAPI.getTopicStats();
        setTopicStats(topicData);
      } catch (error) {
        console.error('Failed to load dashboard data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadDashboardData();
  }, [isFreePlan, subscriptionStatusLoading]);

  useRememberedScroll('dashboard', payload !== null && !isLoading);


  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 shadow border-t-4" style={{ borderTopColor: subjectColour }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <div
                className="h-10 w-10 rounded-lg flex items-center justify-center"
                style={{ backgroundColor: subjectColour }}
                data-testid="subject-logo"
              >
                <svg className="h-6 w-6 text-white" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2L13.09 8.26L22 9L13.09 9.74L12 16L10.91 9.74L2 9L10.91 8.26L12 2Z"/>
                </svg>
              </div>
              <div className="ml-3">
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                  Cracked At
                </h1>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {active?.name ?? 'VCE Practice Platform'}
                </p>
              </div>
              <div className="ml-4">
                <SubjectDropdown />
              </div>
            </div>
            <UserMenu />
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 lg:flex lg:gap-8">
        <SubjectSidebar />
        <main className="flex-1 min-w-0">
        {firstRun && active ? (
          <SubjectFirstRun subject={active} />
        ) : (
        <>
        <SubjectPathHeader />
        {isFreePlan && freeUsage && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow mb-8">
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
              <div>
                <h2 className="text-lg font-medium text-gray-900 dark:text-white">Daily Free Access</h2>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Track today’s free usage and upgrade any time to unlock unlimited sessions.
                </p>
              </div>
              {freeUsageResetLabel && (
                <span className="text-xs font-medium text-gray-500 dark:text-gray-400">
                  Resets {freeUsageResetLabel}
                </span>
              )}
            </div>
            <div className="p-6 grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-4">
                <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">Practice Questions</h3>
                <p className="text-2xl font-bold text-subject-600 dark:text-subject-400">
                  {freeUsage.practice_questions_used}/{freeUsage.practice_questions_limit}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400">per day</p>
              </div>
              <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-4">
                <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">Survival Runs</h3>
                <p className="text-2xl font-bold text-subject-600 dark:text-subject-400">
                  {freeUsage.survival_sessions_used}/{freeUsage.survival_sessions_limit}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400">per day</p>
              </div>
              <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-4">
                <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">Rush Runs</h3>
                <p className="text-2xl font-bold text-subject-600 dark:text-subject-400">
                  {freeUsage.rush_sessions_used}/{freeUsage.rush_sessions_limit}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400">per day</p>
              </div>
            </div>
          </div>
        )}

        {/* Practice Mode */}
        {practiceSection}

        {/* Game Modes */}
        {gameModesSection}

        {/* Skills */}
        {skillsSection}

        {/* Study Tools */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow mb-8">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <h2 className="text-lg font-medium text-gray-900 dark:text-white">Study Tools</h2>
          </div>
          <div className="p-6 space-y-4">
            {MINGU_ENABLED && (
              <Link
                to="/mingu"
                className="block p-4 border border-gray-200 dark:border-gray-700 rounded-lg transition-colors hover:border-subject-500 hover:bg-subject-50 dark:hover:bg-subject-900/20"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div className="p-2 bg-subject-100 dark:bg-subject-900/30 rounded-lg">
                      <svg className="h-6 w-6 text-subject-600 dark:text-subject-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3a6 6 0 00-6 6v3a6 6 0 0012 0V9a6 6 0 00-6-6zm-4 9h8M9 9h.01M15 9h.01M9 16l-2 4m8-4 2 4" />
                      </svg>
                    </div>
                    <div className="ml-4">
                      <h3 className="font-medium text-gray-900 dark:text-white">Mingu AI Tutor</h3>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        Ask for a custom set of 5 checked VCE Methods questions
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center text-sm text-subject-600 dark:text-subject-400">
                    Ask Mingu →
                  </div>
                </div>
              </Link>
            )}

            <Link
              to="/library"
              className="block p-4 border border-gray-200 dark:border-gray-700 rounded-lg transition-colors hover:border-subject-500 hover:bg-subject-50 dark:hover:bg-subject-900/20"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <div className="p-2 bg-indigo-100 dark:bg-indigo-900 rounded-lg">
                    <svg className="h-6 w-6 text-indigo-600 dark:text-indigo-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                    </svg>
                  </div>
                  <div className="ml-4">
                    <div className="flex items-center">
                      <h3 className="font-medium text-gray-900 dark:text-white">Question Library</h3>
                    </div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Browse questions by year and question number for reference
                    </p>
                  </div>
                </div>
                <div className="flex items-center text-sm text-subject-600 dark:text-subject-400">
                  Browse Questions →
                </div>
              </div>
            </Link>

            <Link
              to="/practice/tags"
              className="block p-4 border border-gray-200 dark:border-gray-700 rounded-lg transition-colors hover:border-subject-500 hover:bg-subject-50 dark:hover:bg-subject-900/20"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <div className="p-2 bg-subject-100 dark:bg-subject-900/30 rounded-lg">
                    <svg className="h-6 w-6 text-subject-600 dark:text-subject-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M4 4l7.586-1.172a2 2 0 011.414.414l7.758 7.758a2 2 0 010 2.828l-6.364 6.364a2 2 0 01-2.828 0l-7.758-7.758a2 2 0 01-.414-1.414L4 4z" />
                    </svg>
                  </div>
                  <div className="ml-4">
                    <div className="flex items-center">
                      <h3 className="font-medium text-gray-900 dark:text-white">Practice by Tags</h3>
                    </div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Build a custom session by selecting specific concepts
                    </p>
                  </div>
                </div>
                <div className="flex items-center text-sm text-subject-600 dark:text-subject-400">
                  Select Tags →
                </div>
              </div>
            </Link>

            <Link
              to="/stats"
              className="block p-4 border border-gray-200 dark:border-gray-700 rounded-lg transition-colors hover:border-subject-500 hover:bg-subject-50 dark:hover:bg-subject-900/20"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <div className="p-2 bg-yellow-100 dark:bg-yellow-900 rounded-lg">
                    <svg className="h-6 w-6 text-yellow-600 dark:text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                    </svg>
                  </div>
                  <div className="ml-4">
                    <div className="flex items-center">
                      <h3 className="font-medium text-gray-900 dark:text-white">Stats & Rankings</h3>
                    </div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      View your performance and global leaderboards
                    </p>
                  </div>
                </div>
                <div className="flex items-center text-sm text-subject-600 dark:text-subject-400">
                  View Stats →
                </div>
              </div>
            </Link>
          </div>
        </div>

        {/* Admin Panel - Only show for admin users */}
        {user?.is_admin && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow mb-8">
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-medium text-gray-900 dark:text-white">Admin Panel</h2>
                <span className="px-2 py-1 text-xs font-medium rounded-full bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200">
                  ADMIN
                </span>
              </div>
            </div>
            <div className="p-6">
              <Link 
                to="/admin"
                className="block p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:border-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div className="p-2 bg-red-100 dark:bg-red-900 rounded-lg">
                      <svg className="h-6 w-6 text-red-600 dark:text-red-400" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M12 2C13.1 2 14 2.9 14 4C14 5.1 13.1 6 12 6C10.9 6 10 5.1 10 4C10 2.9 10.9 2 12 2ZM21 9V7L15 7.5V9H21ZM15 10H21V12H15V10ZM5 7L3 7V9H9V7.5L5 7ZM3 10H9V12H3V10ZM19 3H5C3.9 3 3 3.9 3 5V19C3 20.1 3.9 21 5 21H19C20.1 21 21 20.1 21 19V5C21 3.9 20.1 3 19 3ZM19 19H5V5H19V19Z"/>
                      </svg>
                    </div>
                    <div className="ml-4">
                      <h3 className="font-medium text-gray-900 dark:text-white">Platform Management</h3>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        Manage users, view analytics, and system administration
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center text-sm text-red-600 dark:text-red-400">
                    Admin Dashboard →
                  </div>
                </div>
              </Link>
            </div>
          </div>
        )}

        {/* Topic Selection */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow mb-8">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <h2 className="text-lg font-medium text-gray-900 dark:text-white">Practice by Topic</h2>
          </div>
          <div className="p-6">
            {isFreePlan ? (
              <div className="text-center py-8 text-gray-600 dark:text-gray-400">
                Unlock topic analytics and personalised study recommendations with Cracked At Pro.
              </div>
            ) : isLoading ? (
              <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-subject-500"></div>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {topicStats.map((topic, index) => (
                  <Link
                    key={topic.topic}
                    to={`/practice?topic=${encodeURIComponent(topic.topic)}`}
                    className="block border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:border-subject-500 hover:bg-subject-50 dark:hover:bg-subject-900/20 cursor-pointer transition-colors"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="font-medium text-gray-900 dark:text-white">
                        {topic.topic}
                      </h3>
                      <svg className="h-5 w-5 text-subject-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </div>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">
                      {topic.total_questions} questions
                    </p>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                      Avg difficulty: {topic.average_difficulty?.toFixed(1)}
                    </p>
                    <div className="flex items-center text-sm text-subject-600 dark:text-subject-400">
                      Practice {topic.topic} →
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </div>
        </>
        )}
        </main>
      </div>
    </div>
  );
};

export default Dashboard;
